// GPS & Load — Development (2026-10-12). Per outfield player: 1 specialisation + 1 area to improve,
// for Training and for Match, over a fixed period, relative to his reference group (position →
// line → squad). Everything — blocks, reference groups, ratios, picks, sentences — comes from
// GET /api/gps/development/key-points (services/development/); this page only renders it.
// The Trends page is reached from here (header button), no longer from the GPS sub-tab bar.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { getDevelopmentKeyPoints } from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';
import { TRENDS_CSS } from './trendsShared';
import DevelopmentPlayerDrawer from './DevelopmentPlayerDrawer';
import {
  DEV_CSS, BLOCKS, BLOCK_LABEL, POSITION_ORDER, keyPointShort, stateShort, referenceBadge, referenceText, fmtDate,
} from './developmentShared';

function useIsNarrow(maxWidth = 899) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return narrow;
}

const th = { textAlign: 'left', padding: '8px 8px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
const td = { padding: '8px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: 12.5, color: 'var(--text-primary)' };
const COLUMNS = BLOCKS.flatMap(b => [
  { block: b, kind: 'specialisation', label: `${BLOCK_LABEL[b]}: specialisation` },
  { block: b, kind: 'improvement', label: `${BLOCK_LABEL[b]}: area to improve` },
]);

const openKeys = onOpen => e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } };

function PlayerName({ p }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{p.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {p.positionGroup}{p.positionGroup !== p.position ? ` (squad ${p.position})` : ''} · {p.minutesInWindow} min
      </div>
    </div>
  );
}

function Badge({ block }) {
  const b = referenceBadge(block);
  if (!b) return null;
  return <span className="dev-badge" title={`${b.title}. ${referenceText(block) || ''}`}>{b.label}</span>;
}

// One key-point cell's content (no wrapper). kind: 'specialisation' | 'improvement'.
function KeyPointText({ block, kind, withBadge }) {
  const kp = block.keyPoints[kind];
  const picked = kp && kp.kind !== 'none';
  return (
    <span title={kp?.text} style={{ color: picked ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: picked ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>
      {keyPointShort(kp)}
      {withBadge && <Badge block={block} />}
    </span>
  );
}

function StateText({ blockKey, block }) {
  return (
    <span title={block.keyPoints.state?.text} style={{ color: 'var(--text-muted)' }}>
      — {stateShort(blockKey, block)}
    </span>
  );
}

const accent = (block, kind) => {
  const kp = block.keyPoints[kind];
  if (!kp || kp.kind === 'none') return 'dev-cell';
  return `dev-cell ${kind === 'specialisation' ? 'dev-spec' : 'dev-imp'}`;
};

function KeyPointsTable({ players, onOpen }) {
  let lastPos = null;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Player</th>
            {COLUMNS.map(c => <th key={`${c.block}-${c.kind}`} style={th}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {players.map(p => {
            const divider = p.positionGroup !== lastPos;
            lastPos = p.positionGroup;
            const open = () => onOpen(p.playerId);
            return (
              <React.Fragment key={p.playerId}>
                {divider && (
                  <tr><td colSpan={COLUMNS.length + 1} style={{ padding: '12px 8px 3px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-dimmed)', letterSpacing: 0.4 }}>{p.positionGroup}</td></tr>
                )}
                <tr className="tr-row" tabIndex={0} onClick={open} onKeyDown={openKeys(open)} aria-label={`${p.name} — open details`}>
                  <td style={td}><PlayerName p={p} /></td>
                  {BLOCKS.map(b => (p[b].keyPoints.state
                    ? <td key={b} colSpan={2} style={td}><StateText blockKey={b} block={p[b]} /></td>
                    : ['specialisation', 'improvement'].map(kind => (
                      <td key={`${b}-${kind}`} style={td}>
                        <div className={accent(p[b], kind)}><KeyPointText block={p[b]} kind={kind} withBadge={kind === 'specialisation'} /></div>
                      </td>
                    ))))}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PlayerCard({ p, onOpen }) {
  const open = () => onOpen(p.playerId);
  return (
    <div className="tr-row" tabIndex={0} role="button" aria-label={`${p.name} — open details`} onClick={open} onKeyDown={openKeys(open)}
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <PlayerName p={p} />
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {BLOCKS.map(b => (
          <div key={b}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>
              {BLOCK_LABEL[b]}{!p[b].keyPoints.state && <Badge block={p[b]} />}
            </div>
            {p[b].keyPoints.state ? (
              <div style={{ fontSize: 12.5 }}><StateText blockKey={b} block={p[b]} /></div>
            ) : (
              <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                {['specialisation', 'improvement'].map(kind => (
                  <div key={kind} className={accent(p[b], kind)}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{kind === 'specialisation' ? 'Specialisation: ' : 'Area to improve: '}</span>
                    <KeyPointText block={p[b]} kind={kind} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading key points" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div className="tr-skel" style={{ width: 160, height: 30 }} />
          {COLUMNS.map(c => <div key={`${c.block}-${c.kind}`} className="tr-skel" style={{ flex: 1, height: 30 }} />)}
        </div>
      ))}
    </div>
  );
}

function Message({ title, body }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
      {body && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>{body}</div>}
    </div>
  );
}

const sortPlayers = list => [...list].sort((a, b) => {
  const pa = POSITION_ORDER.indexOf(a.positionGroup), pb = POSITION_ORDER.indexOf(b.positionGroup);
  return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb) || a.name.localeCompare(b.name);
});

// ── Page ───────────────────────────────────────────────────────────────────────────────────────
export default function Development({ user, onNavigate }) {
  const narrow = useIsNarrow();
  const [period, setPeriod] = useUrlParam('period', '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const token = user?.sessionToken;

  const load = useCallback(() => {
    if (!token) { setLoading(false); setError('session'); return Promise.resolve(); }
    setLoading(true); setError(null);
    return getDevelopmentKeyPoints(token, period)
      .then(d => setData(d))
      .catch(e => setError(String(e.message || e).includes('401') ? 'session' : 'load'))
      .finally(() => setLoading(false));
  }, [token, period]);

  useEffect(() => { load(); }, [load]);
  const closeDrawer = useCallback(() => setOpenId(null), []);

  const players = useMemo(() => sortPlayers(data?.players || []), [data]);
  const hasAnyData = players.some(p => BLOCKS.some(b => !p[b].keyPoints.state));
  const openPlayer = openId != null ? players.find(p => p.playerId === openId) : null;
  const meta = data?.meta;
  const periods = meta?.periods || [];

  return (
    <div className="trends-root dev-root" style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 40 }}>
      <style>{TRENDS_CSS}{DEV_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Development{meta ? ` — ${meta.period.label} (${fmtDate(meta.period.from)} – ${fmtDate(meta.period.to)})` : ''}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Relative to position group · provisional ±10% thresholds · a low value is not necessarily a weakness (role, tactics)
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {periods.length > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              Period
              <select value={period || meta?.period.id || ''} onChange={e => setPeriod(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12 }}>
                {periods.map(p => <option key={p.id} value={p.id}>{p.label} ({fmtDate(p.from)} – {fmtDate(p.to)})</option>)}
              </select>
            </label>
          )}
          <button type="button" className="dev-btn" onClick={() => onNavigate && onNavigate('gps', 'trends')}>
            <TrendingUp size={14} /> Trends
          </button>
        </div>
      </div>

      {meta && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 12px', marginBottom: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
          <div>
            Match: {meta.matchesIncluded.length} competitive matches, 60+ min, per 90
            {meta.matchesExcluded.length > 0 && <span style={{ color: 'var(--text-muted)' }}> ({meta.matchesExcluded.length} friendly left out)</span>}
            {' · '}Training: {meta.weeksIncluded.length} complete weeks vs squad median
            {meta.weeksExcluded.length > 0 && (
              <span style={{ color: 'var(--text-muted)' }}> ({meta.weeksExcluded.map(w => `W${Number(w.isoWeek.split('-W')[1])} ${w.reason === 'no_data' ? 'no data' : 'not complete'}`).join(', ')})</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            <span className="dev-cell dev-spec">above group</span>{'  '}<span className="dev-cell dev-imp" style={{ marginLeft: 10 }}>below group</span>
          </div>
        </div>
      )}

      {error === 'session' ? (
        <Message title="Session expired" body="Log in again to view Development." />
      ) : error ? (
        <Message title="Key points could not be loaded" body={<button className="dev-btn" onClick={load} style={{ marginTop: 8 }}>Try again</button>} />
      ) : loading && !data ? (
        <Skeleton />
      ) : !hasAnyData ? (
        <Message title="No data for this period" body="No outfield player has enough matches or training weeks in this period yet." />
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          {narrow ? (
            <div style={{ display: 'grid', gap: 10 }}>{players.map(p => <PlayerCard key={p.playerId} p={p} onOpen={setOpenId} />)}</div>
          ) : (
            <section style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
              <KeyPointsTable players={players} onOpen={setOpenId} />
            </section>
          )}
        </div>
      )}

      {openPlayer && <DevelopmentPlayerDrawer player={openPlayer} fullScreen={narrow} onClose={closeDrawer} />}
    </div>
  );
}
